import {useState} from "react"
import {Link} from "react-router-dom"
import {default as cx} from "classnames"
import OverlayTrigger from "react-bootstrap/OverlayTrigger"
import Popover from "react-bootstrap/Popover"

import {getToken} from "../../utilities/useToken"
import {wrappedPost} from "../../utilities/AxiosHelper"

import SurveyDropdownMenu from "./SurveyDropdownMenu"
import MetricsDropdownMenu from "./MetricsDropdownMenu"
import AccountDropdownMenu from "./AccountDropdownMenu"
import AdminDropdownMenu from "./AdminDropdownMenu"
import HelpDropdownMenu from "./HelpDropdownMenu"

import OarsLogo from "../../static/img/oars-logo.svg"
import {NHSR_ROLES, OSR_ROLES} from "../../utilities/roleAccess"
import {METRIC_NAVBAR_LINKS} from "./RouteLinks"
import {calculateMetricsRoutes} from "../../utilities/general"

const MenuItem = ({isActive, path, title, icon = ""}) => {
    return (
        <div className="d-flex align-items-stretch">
            <div className="app-header-menu app-header-mobile-drawer align-items-stretch">
                <div className="menu here menu-column menu-lg-row menu-active-bg menu-title-gray-900 menu-state-primary menu-arrow-gray-900 fw-semibold fw-semibold fs-6 align-items-stretch my-5 my-lg-0 px-2 px-lg-0">
                    <div className={(isActive ? "here show " : "") + "menu-item"}>
                        <Link to={path} className="menu-link">
                            {icon !== "" && (
                                <span className="menu-icon">
                                    <i className={cx("fas fs-2 text-white", icon)}></i>
                                </span>
                            )}
                            <span className="menu-title">{title}</span>
                        </Link>
                    </div>
                </div>
            </div>
        </div>
    )
}

function NavBar(props) {
    const {display_name: userAbbr, user_name: userName, role} = getToken(true) || {}

    const METRIC_LINKS = calculateMetricsRoutes(METRIC_NAVBAR_LINKS, role)
    const [resettingUnit, setResettingUnit] = useState(false)

    // OARS-877: move the current user onto a brand-new training unit.
    const handleResetUnit = async () => {
        if (resettingUnit) return
        const confirmed = window.confirm(
            "Reset your unit?\n\nYou will be moved to a brand-new training unit with a fresh set of orientations. " +
                "Your current unit will no longer be available to you."
        )
        if (!confirmed) return

        setResettingUnit(true)
        try {
            const response = await wrappedPost("training/unit/reset")
            sessionStorage.setItem("unit_name", response.data.unit_name || "")
            // Unit-scoped data is everywhere; reloading home refreshes all of it.
            window.location.href = "/home"
        } catch (error) {
            setResettingUnit(false)
            window.alert(error.response?.data?.error || "Unable to reset your unit. Please contact the Help Desk.")
        }
    }

    return (
        <>
            <header className="header app-header">
                <div className="app-header-primary container-fluid d-flex align-items-center justify-content-between">
                    <div className="d-flex align-items-center justify-content-between flex-row-fluid">
                        <Link to="/home" className="text-white fs-4 fw-medium text-decoration-none">
                            <OarsLogo className="h-25px logo" />
                        </Link>

                        <div className="d-flex align-items-center d-none d-lg-flex">
                            {role === "Preceptor" && (
                                <>
                                    {/* <MenuItem
                                        path="/preceptor-metrics"
                                        isActive={props.currentPath.startsWith("/preceptor-metrics")}
                                        title="My Metrics"
                                        icon="fa-hospital-user"
                                    /> */}
                                    <MenuItem
                                        path="/preceptor-orientations"
                                        isActive={props.currentPath.startsWith("/preceptor-orientations")}
                                        title="My Orientees"
                                        icon="fa-user-nurse"
                                    />
                                    <MenuItem
                                        path="/preceptor-completed-orientations"
                                        isActive={props.currentPath.startsWith("/preceptor-completed-orientations")}
                                        title="Archived Orientees"
                                        icon="fa-box-archive"
                                    />
                                </>
                            )}

                            {["Administrator", "Educator", "Enterprise Admin"].includes(role) && (
                                <>
                                    <MenuItem
                                        path="/educator-onboarding"
                                        isActive={props.currentPath.startsWith("/educator-onboarding")}
                                        title="Onboard Orientees"
                                        icon="fa-list-check"
                                    />
                                    <MenuItem
                                        path="/educator-orientations"
                                        isActive={
                                            props.currentPath.startsWith("/educator-orientations") &&
                                            !props.currentSearch?.endsWith("?archived=true")
                                        }
                                        title="Active Orientees"
                                        icon="fa-user-nurse"
                                    />
                                    <MenuItem
                                        path="/educator-orientations?archived=true"
                                        isActive={
                                            props.currentPath.startsWith("/educator-orientations") &&
                                            props.currentSearch?.endsWith("?archived=true")
                                        }
                                        title="Archived Orientees"
                                        icon="fa-clipboard-user"
                                    />
                                    {window.__APP_CONFIG__?.ENV.toLowerCase() === 'train' && (
                                    <MenuItem
                                        path="/educator-preceptor-metrics"
                                        isActive={
                                            props.currentPath.startsWith("/educator-preceptor-metrics") &&
                                            props.currentSearch?.endsWith("/educator-preceptor-metrics")
                                        }
                                        title="Preceptors"
                                        icon="fa-hospital-user"
                                    />
                                    )}
                                </>
                            )}

                            {[...NHSR_ROLES, ...OSR_ROLES].includes(role) && role !== "Administrator" && (
                                <OverlayTrigger
                                    trigger="click"
                                    placement="bottom"
                                    rootClose
                                    overlay={
                                        <Popover style={{position: "fixed !important"}}>
                                            <SurveyDropdownMenu />
                                        </Popover>
                                    }
                                    popperConfig={{
                                        modifiers: [
                                            {name: "flip", enabled: false},
                                            {name: "preventOverflow", options: {altAxis: true}},
                                        ],
                                    }}
                                >
                                    <div className="app-header-menu">
                                        <div className="menu">
                                            <div className="menu-item">
                                                <a href="" onClick={(e) => e.preventDefault()} className="menu-link">
                                                    <i className="fas fs-2 text-white fa-clipboard-list me-sm-3" />
                                                    <span className="menu-title">Surveys & Reports</span>
                                                </a>
                                            </div>
                                        </div>
                                    </div>
                                </OverlayTrigger>
                            )}
                            {METRIC_LINKS.length > 0 && (
                                <OverlayTrigger
                                    trigger="click"
                                    placement="bottom"
                                    rootClose
                                    overlay={
                                        <Popover style={{position: "fixed !important"}}>
                                            <MetricsDropdownMenu allowedRoutes={METRIC_LINKS} />
                                        </Popover>
                                    }
                                    popperConfig={{
                                        modifiers: [
                                            {name: "flip", enabled: false},
                                            {name: "preventOverflow", options: {altAxis: true}},
                                        ],
                                    }}
                                >
                                    <div className="app-header-menu">
                                        <div className="menu">
                                            <div className="menu-item">
                                                <a href="" onClick={(e) => e.preventDefault()} className="menu-link">
                                                    <i className="fas fs-2 text-white fa-clipboard-list me-sm-3" />
                                                    <span className="menu-title">Metrics & Scores</span>
                                                </a>
                                            </div>
                                        </div>
                                    </div>
                                </OverlayTrigger>
                            )}
                        </div>

                        <div className="d-flex align-items-center gap-2 gap-md-3 d-none d-lg-flex">
                            <OverlayTrigger
                                trigger="click"
                                placement="bottom"
                                rootClose
                                overlay={
                                    <Popover style={{position: "fixed !important"}}>
                                        <HelpDropdownMenu />
                                    </Popover>
                                }
                                popperConfig={{
                                    modifiers: [
                                        {name: "flip", enabled: false},
                                        {name: "preventOverflow", options: {altAxis: true}},
                                    ],
                                }}
                            >
                                <div className="p-4 dropdown-toggle btn btn-dark no-caret" onClick={() => {}}>
                                    <i className="fas fa-circle-question fs-1 text-white p-0"></i>
                                </div>
                            </OverlayTrigger>

                            {["Enterprise Admin", "Administrator", "Educator"].includes(role) && (
                                <OverlayTrigger
                                    trigger="click"
                                    placement="bottom"
                                    rootClose
                                    overlay={
                                        <Popover style={{position: "fixed !important"}}>
                                            <AdminDropdownMenu role={role} />
                                        </Popover>
                                    }
                                    popperConfig={{
                                        modifiers: [
                                            {name: "flip", enabled: false},
                                            {name: "preventOverflow", options: {altAxis: true}},
                                        ],
                                    }}
                                >
                                    <div className="p-4 dropdown-toggle btn btn-dark no-caret">
                                        <i className="fas fa-user-gear fs-1 text-white p-0"></i>
                                    </div>
                                </OverlayTrigger>
                            )}

                            <OverlayTrigger
                                trigger="click"
                                placement="bottom"
                                rootClose
                                overlay={
                                    <Popover style={{position: "fixed !important"}}>
                                        <AccountDropdownMenu
                                            userAbbr={userAbbr}
                                            userName={userName}
                                            role={role}
                                            darkMode={props.darkMode}
                                            onClickLogout={handleLogout}
                                            setDarkMode={props.setDarkMode}
                                            // OARS-877: Enterprise Admins don't get training units
                                            onClickResetUnit={role !== "Enterprise Admin" ? handleResetUnit : undefined}
                                            resettingUnit={resettingUnit}
                                        />
                                    </Popover>
                                }
                                popperConfig={{
                                    modifiers: [
                                        {name: "flip", enabled: false},
                                        // {name: "preventOverflow", options: {altAxis: true}},
                                        {
                                            name: "preventOverflow",
                                            options: {
                                                padding: 24,
                                                altAxis: true,
                                            },
                                        },
                                    ],
                                }}
                            >
                                <div className="p-3 dropdown-toggle btn btn-dark no-caret">
                                    <span className="fs-2 fw-semibold text-white">{userAbbr}</span>
                                </div>
                            </OverlayTrigger>
                        </div>

                        <Link
                            to="/menu"
                            className="d-flex align-items-center d-lg-none"
                            title="Open menu"
                            aria-label="Open menu"
                        >
                            <div className="btn btn-icon btn-active-color-primary w-30px h-30px w-md-40px h-md-40px">
                                <i className="ki-outline ki-abstract-14 fs-1"></i>
                            </div>
                        </Link>
                    </div>
                </div>
            </header>
        </>
    )

    // return (
    //     <>
    //         <header className="header app-header">
    //             <div className="app-header-primary container-fluid d-flex align-items-center justify-content-between">
    //                 <div className="d-flex align-items-center justify-content-between flex-row-fluid">
    //                     <Link to="/home" className="text-white fs-4 fw-medium text-decoration-none">
    //                         <OarsLogo className="h-25px logo" />
    //                     </Link>
    //                     <div className="d-flex align-items-center">
    //                         {["Admin", "Unit Administrator", "Educator"].includes(role) && (
    //                             <MenuItem
    //                                 title="My Team"
    //                                 path="/educator-orientees"
    //                                 icon="fa-sitemap"
    //                                 isActive={props.currentPath.startsWith("/educator-orientees")}
    //                             />
    //                         )}
    //                         {[...NHSR_ROLES, ...OSR_ROLES].includes(role) && <SurveyDropdownMenu />}
    //                         {[...SYSTEM_DASHBOARD_ROLES, ...UNIT_DASHBOARD_ROLES].includes(role) && (
    //                             <MetricsDropdownMenu />
    //                         )}
    //                         {[...UNIT_DASHBOARD_ROLES].includes(role) && (
    //                             <MenuItem
    //                                 path="/orientees"
    //                                 isActive={props.currentPath.startsWith("/orientees")}
    //                                 title="Orientees"
    //                                 icon="fa-user-nurse"
    //                             />
    //                         )}
    //                         {role === "Preceptor" && (
    //                             <>
    //                                 {preceptorOptions.map((option) => (
    //                                     <MenuItem
    //                                         key={option.path}
    //                                         path={option.path}
    //                                         isActive={props.currentPath.startsWith(option.path)}
    //                                         title={option.title}
    //                                         icon={option.icon}
    //                                     />
    //                                 ))}
    //                                 <div className="d-flex flex-row-fluid bg-white shadow-sm fixed-bottom d-lg-none p-0">
    //                                     <div className="app-container container-fluid">
    //                                         <div className="toolbar">
    //                                             <div className="d-flex align-items-stretch flex-center">
    //                                                 <div className="align-items-center d-flex gap2">
    //                                                     {preceptorOptions.map((option) => (
    //                                                         <ToolbarLink
    //                                                             key={option.path}
    //                                                             path={option.path}
    //                                                             title={option.title}
    //                                                             icon={option.icon}
    //                                                             active={props.currentPath === option.path}
    //                                                         />
    //                                                     ))}
    //                                                 </div>
    //                                             </div>
    //                                         </div>
    //                                     </div>
    //                                 </div>
    //                             </>
    //                         )}
    //                     </div>
    //                     <div className="d-flex align-items-center gap-2 gap-md-3">
    //                         <HelpDropdownMenu />

    //                         <AdminDropdownMenu role={role} />

    //                         <AccountDropdownMenu
    //                             userAbbr={userAbbr}
    //                             userName={userName}
    //                             role={role}
    //                             darkMode={props.darkMode}
    //                             setDarkMode={props.setDarkMode}
    //                         />
    //                     </div>
    //                 </div>
    //             </div>
    //         </header>
    //     </>
    // )
}

export const handleLogout = (isTimeout) => {
    const {access_token} = getToken(true)
    if (access_token) {
        window.location.replace(`/logoutnav?isTimeout=${isTimeout}`)
    }
}

export default NavBar
